import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';

import { of } from 'rxjs';

import {
  IAddressValue,
  InputAddress,
  InputDropdown,
  InputEmail,
  InputLogo,
  InputPanel,
  InputPhone,
  InputTextarea,
  InputTextBox,
} from '@ta/form-model';
import { extractEnum } from '@ta/utils';

import { companyRevenueLabel } from 'src/app/features/companies/components/company-revenue/company-revenue';

import { Company } from '../dto/company';
import { CompanyRevenue } from '../dto/revenue';
import { CreateCompanyInput } from './dto/companyInput';

export enum ECompanyFormFields {
  name = 'name',
  logo = 'logo',
  description = 'description',
  registrationNumber = 'registrationNumber',
  vatNumber = 'vatNumber',
  email = 'email',
  phoneNumber = 'phoneNumber',
  website = 'website',
  address = 'address',
  revenue = 'revenue',
}

const PANEL_CLASS = 'card highlight-title';

@Injectable({
  providedIn: 'root',
})
export class CompanyFormService {
  public getCompanyForm(company?: Company | null, readonly = false) {
    return [
      new InputPanel({
        key: 'panel_root',
        contentClass: 'grid g-space-md',
        children: [
          new InputPanel({
            key: 'panel_company_logo',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.logo',
            children: [
              new InputLogo({
                key: ECompanyFormFields.logo,
                value: {
                  description: '',
                  id: '',
                  url: company?.logo ?? '',
                  size: 0,
                },
                disabled: readonly,
              }),
            ],
          }),
          new InputPanel({
            key: 'panel_company_identity',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.identity',
            contentClass: 'grid g-space-md',
            children: [
              new InputTextBox({
                key: ECompanyFormFields.name,
                label: 'app.forms.company.field.name',
                validators: [Validators.required],
                value: company?.name,
                class: 'full',
                disabled: readonly,
              }),
              new InputTextBox({
                key: ECompanyFormFields.registrationNumber,
                label: 'app.forms.company.field.registration_number',
                value: company?.registrationNumber,
                class: 'mobile-full one-half',
                disabled: readonly,
              }),
              new InputTextBox({
                key: ECompanyFormFields.vatNumber,
                label: 'app.forms.company.field.vat_number',
                value: company?.vatNumber,
                class: 'mobile-full one-half',
                disabled: readonly,
              }),
              new InputTextarea({
                key: ECompanyFormFields.description,
                label: 'app.forms.company.field.description',
                value: company?.description,
                class: 'full',
                disabled: readonly,
              }),
            ],
          }),
          new InputPanel({
            key: 'panel_company_address',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.address',
            contentClass: 'grid g-space-md',
            children: [
              new InputAddress({
                key: ECompanyFormFields.address,
                value: company?.address,
                class: 'full',
                disabled: readonly,
              }),
            ],
          }),
          new InputPanel({
            key: 'panel_company_contact',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.contact',
            contentClass: 'grid g-space-md',
            children: [
              new InputEmail({
                key: ECompanyFormFields.email,
                label: 'app.forms.company.field.email',
                value: company?.email,
                class: 'mobile-full one-half',
                disabled: readonly,
              }),
              new InputPhone({
                key: ECompanyFormFields.phoneNumber,
                label: 'app.forms.company.field.phone_number',
                value: company?.phoneNumber,
                class: 'mobile-full one-half',
                disabled: readonly,
              }),
              new InputTextBox({
                key: ECompanyFormFields.website,
                label: 'app.forms.company.field.website',
                value: company?.website,
                class: 'full',
                disabled: readonly,
              }),
            ],
          }),
          new InputPanel({
            key: 'panel_company_size',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.size',
            children: [
              new InputDropdown({
                key: ECompanyFormFields.revenue,
                label: 'app.forms.company.field.revenue',
                value: (company?.revenue ?? CompanyRevenue.NONE).toString(),
                options$: of(
                  extractEnum(CompanyRevenue).map((item) => ({
                    id: item.value.toString(),
                    name: companyRevenueLabel(item.value) ?? '',
                  })),
                ),
                class: 'full',
                disabled: readonly,
              }),
            ],
          }),
        ],
      }),
    ];
  }

  /** Formulaire réduit de la modale d'inscription : uniquement l'identité minimale de la société. */
  public getCreationForm() {
    return [
      new InputPanel({
        key: 'panel_root',
        contentClass: 'grid g-space-md',
        children: [
          new InputPanel({
            key: 'panel_company_creation',
            class: 'full',
            containerClass: [PANEL_CLASS],
            label: 'app.forms.company.panel.identity',
            contentClass: 'grid g-space-md',
            children: [
              new InputTextBox({
                key: ECompanyFormFields.name,
                label: 'app.forms.company.field.name',
                validators: [Validators.required],
                class: 'full',
              }),
              new InputTextBox({
                key: ECompanyFormFields.registrationNumber,
                label: 'app.forms.company.field.registration_number',
                validators: [Validators.required],
                class: 'full',
              }),
            ],
          }),
        ],
      }),
    ];
  }

  public formatCreationForm(data: unknown): Partial<CreateCompanyInput> {
    const form = data as Record<string, unknown>;
    return {
      name: (form[ECompanyFormFields.name] as string) ?? '',
      registrationNumber: (form[ECompanyFormFields.registrationNumber] as string) ?? '',
      revenue: CompanyRevenue.NONE,
    };
  }

  public formatCompanyForm(data: unknown): Partial<CreateCompanyInput> {
    const form = data as Record<string, unknown>;
    return {
      name: (form[ECompanyFormFields.name] as string) ?? '',
      description: (form[ECompanyFormFields.description] as string) ?? '',
      registrationNumber: (form[ECompanyFormFields.registrationNumber] as string) ?? '',
      vatNumber: (form[ECompanyFormFields.vatNumber] as string) ?? '',
      website: (form[ECompanyFormFields.website] as string) ?? '',
      email: (form[ECompanyFormFields.email] as string) ?? '',
      phoneNumber: (form[ECompanyFormFields.phoneNumber] as string) ?? '',
      logo: (form[ECompanyFormFields.logo] as { url?: string } | undefined)?.url ?? '',
      revenue: Number(form[ECompanyFormFields.revenue] ?? CompanyRevenue.NONE) as CompanyRevenue,
    };
  }

  public extractAddress(data: unknown): Partial<IAddressValue> | undefined {
    const form = data as Record<string, unknown>;
    return form[ECompanyFormFields.address] as Partial<IAddressValue> | undefined;
  }
}
